// ============================================
// Patient Chat Engine — Unified Three-Mode Chat
// SEEN -> RIGHT -> SAFE -> ACT
//
// SEEN:  She feels heard before giving data
// RIGHT: Her doctor was wrong. Here's proof.
// SAFE:  Supplements first. Low cost. Retest.
// ACT:   One tap. Start the protocol.
//
// Accepts ChatDataSource to power demo/live/twin modes.
// Data comes from the data source. Timing is choreography.
//
// After demo auto-play, input field appears for live
// interactive testing with real Gemini responses.
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  ChatDataSource,
  BarMarker,
  BarSymptom,
  BarProtocol,
} from '../../types/analystBar';
import type { CapturedSignal } from '../../types/fashionPlate';
import type { AnalystPhase2Output, ModifierSet } from '../../types/patientJourney';
import { EMPTY_MODIFIER_SET } from '../../types/patientJourney';
import type { GeminiMessage } from '../../services/ai/providers/gemini';
import { AnalystBar } from './analyst-bar/AnalystBar';
import { useApp } from '../../context/AppContext';
import { SaveProgressBanner } from '../shared/SaveProgressBanner';
import { SEED_SUPPLEMENTS } from '../../data/formulary/seedSupplements';
import { SEED_RX } from '../../data/formulary/seedRx';
import { SEED_LONGEVITY } from '../../data/formulary/seedLongevity';
import { parseMonthlyPrice } from '../../services/patientJourney/twinEngine';
import { EmailSaveModal } from '../modals/EmailSaveModal';

// All products for price lookup (supplements + Rx + longevity)
const ALL_PRODUCTS_FOR_PRICE = [...SEED_SUPPLEMENTS, ...SEED_RX, ...SEED_LONGEVITY];
import { DEMO_BIOMARKERS } from '../../services/chat/demoDataSource';
import { mapBiomarkerToBarMarker } from '../../services/agents/labAnalystStream';
import { T, F } from '../quality/tokens';
import { PregnancyModal } from '../modals/PregnancyModal';
import type { NurseOrchestrator } from '../../services/nurse/NurseOrchestrator';

// Chat-specific layout values derived from token system
const R = 4;                  // CLAUDE.md mandates 4px border-radius (no token match)
const R_LG = T.radius.lg;    // bubble rounding — uses token scale
const CONTRAST_WHITE = '#FFFFFF'; // text-on-accent contrast color

// -- Demo biomarkers from demoDataSource, pre-mapped to BarMarker format --
const DEMO_MARKERS: BarMarker[] = DEMO_BIOMARKERS.map(mapBiomarkerToBarMarker);

// -- Map CapturedSignal[] to BarSymptom[] --
function signalsToBarSymptoms(signals: CapturedSignal[]): BarSymptom[] {
  return signals
    .filter((s) => s.category === 'symptom')
    .map((s) => ({
      term: s.term.toUpperCase(),
      sourceText: s.sourceText,
      linkedBiomarker: s.linkedBiomarker,
    }));
}

// -- Build nurse system prompt with full patient context --
function buildNurseSystemPrompt(
  biomarkers: typeof DEMO_BIOMARKERS,
  protocol: AnalystPhase2Output,
  signals: CapturedSignal[],
  brandName: string,
  mdName: string,
  reinforcement: string,
): string {
  const bioSummary = biomarkers
    .filter((b) => b.hemologixStatus !== 'optimal')
    .map((b) => `${b.biomarker}: ${b.value} ${b.unit} (functional: ${b.functionalRange.low}-${b.functionalRange.high}, status: ${b.hemologixStatus})`)
    .join('\n');

  const protocolSummary = protocol.protocol
    .map((p) => `- ${p.productName}: ${p.dosage} (for ${p.triggerBiomarker})`)
    .join('\n');

  const signalSummary = signals.length > 0
    ? signals.map((s) => `- ${s.term} (${s.category})`).join('\n')
    : 'None yet';

  return `You are a ${brandName} health navigator working under ${mdName}'s clinical methodology.

ROLE: You are the Flight Attendant — warm, knowledgeable, never diagnostic. You identify gaps vs optimal ranges, NOT diagnoses. You never say "you have" or "you suffer from." You say "your levels suggest" or "this pattern is consistent with."

PATIENT CONTEXT: Female, 38, presented with fatigue. Labs analyzed. Protocol recommended.

BIOMARKERS (suboptimal/flagged):
${bioSummary}

RECOMMENDED PROTOCOL (Gate 1 — supplements only):
${protocolSummary}

CAPTURED SIGNALS FROM CONVERSATION:
${signalSummary}

RULES:
- Reference specific biomarker values when relevant (e.g., "your Ferritin at 22")
- Never fabricate data — only reference biomarkers listed above
- Never diagnose disease — identify gaps vs optimal ranges
- Never contradict the protocol — it was built by the formulary engine
- If patient mentions a NEW medication, allergy, or condition, acknowledge it and note it may affect the protocol
- Keep responses concise (2-4 sentences). Warm but efficient.
- No emojis. Professional but approachable tone.
${reinforcement ? `\nREINFORCEMENT:\n${reinforcement}` : ''}`;
}

// -- Bidirectional Highlighting --
function HighlightedText({ text, activeSymptoms }: { text: string; activeSymptoms: BarSymptom[] }) {
  if (!activeSymptoms || !activeSymptoms.length) return <>{text}</>;

  const matches: { start: number; end: number; word: string }[] = [];
  const textLower = text.toLowerCase();

  for (const symptom of activeSymptoms) {
    if (!symptom.sourceText) continue;
    const idx = textLower.indexOf(symptom.sourceText.toLowerCase());
    if (idx >= 0) {
      matches.push({ start: idx, end: idx + symptom.sourceText.length, word: text.slice(idx, idx + symptom.sourceText.length) });
    }
  }

  if (!matches.length) return <>{text}</>;
  matches.sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let c = 0;
  for (const m of matches) {
    if (m.start < c) continue;
    if (m.start > c) parts.push(<span key={c}>{text.slice(c, m.start)}</span>);
    parts.push(
      <span key={m.start} style={{
        background: 'rgba(255,255,255,0.28)', borderRadius: 3,
        padding: '1px 5px', margin: '0 -2px', fontWeight: 700,
        borderBottom: '2px solid #FFFFFF',
      }}>{m.word}</span>,
    );
    c = m.end;
  }
  if (c < text.length) parts.push(<span key={c}>{text.slice(c)}</span>);
  return <>{parts}</>;
}

// -- Icons --
const HeartPulseIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" /><path d="M3.22 12H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" /></svg>;
const ShieldIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textGhost} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>;
const FileCheckIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.success} strokeWidth="2" strokeLinecap="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><path d="m9 15 2 2 4-4" /></svg>;
const CheckIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={CONTRAST_WHITE} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>;
const SendIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={CONTRAST_WHITE} strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>;

function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '4px 0' }}>
      {[0, 1, 2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: T.accent, animation: `td 1.4s ease-in-out ${i * 0.16}s infinite` }} />)}
    </div>
  );
}

// ==============================
// CONVERSION T.bgCard -- THE ACT MOMENT
// ==============================
function ProtocolCard({ dataSource, dailyPrice }: { dataSource: ChatDataSource; dailyPrice: string }) {
  const [started, setStarted] = useState(false);
  const bd = dataSource.brandDisplay;

  const trustSignals = [
    `${bd.medicalDirectorName}'s methodology`,
    `Retest at ${bd.retestWeeks} weeks`,
    'Cancel anytime',
  ];

  const objectionKillers = [
    'No prescription needed',
    `Ships in ${bd.shippingDays} days`,
    ...(bd.hsaFsaEligible ? ['HSA/FSA eligible'] : []),
  ];

  return (
    <div style={{
      margin: '8px 0 4px', padding: '16px', background: T.bgCard, border: `1px solid ${T.border}`,
      borderRadius: R_LG, boxShadow: T.shadowSm, animation: 'msgIn 0.3s ease',
    }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
        {trustSignals.map(t => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body }}>
            <div style={{ width: 4, height: 4, borderRadius: '50%', background: T.success, flexShrink: 0 }} />
            {t}
          </div>
        ))}
      </div>

      <button
        onClick={() => setStarted(true)}
        style={{
          width: '100%', padding: '14px 20px', fontSize: T.fontSize.base, fontWeight: 700,
          color: CONTRAST_WHITE, background: started ? T.success : T.accent,
          border: 'none', borderRadius: R, cursor: 'pointer',
          fontFamily: F.body,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'all 0.3s ease',
          boxShadow: started ? 'none' : T.shadowAccent,
        }}
      >
        {started ? <><CheckIcon /> Protocol Started</> : `Start My Protocol \u2014 $${dailyPrice}/day`}
      </button>

      {!started && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10 }}>
          {objectionKillers.map(t => (
            <span key={t} style={{ fontSize: T.fontSize.xs, color: T.textGhost, fontFamily: F.body }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ==============================
// MAIN -- SEEN -> RIGHT -> SAFE -> ACT -> INTERACTIVE
// ==============================
interface ChatMessage {
  id: number;
  role: 'nurse' | 'user' | 'system';
  text: string;
}

interface PatientChatEngineProps {
  dataSource: ChatDataSource;
  orchestrator?: NurseOrchestrator | null;
  onProtocolStart?: (protocol: AnalystPhase2Output) => void;
}

export function PatientChatEngine({ dataSource, orchestrator, onProtocolStart }: PatientChatEngineProps) {
  const { state: appState } = useApp();
  const resumedChatRef = useRef(appState.chatHistory);

  // Use real biomarkers from lab report when available, demo data as fallback
  const isLive = dataSource.mode === 'live' && appState.labReport && !appState.labReport.isMockData;
  const activeBiomarkers = isLive ? appState.labReport!.biomarkers : DEMO_BIOMARKERS;
  const activeMarkers: BarMarker[] = isLive
    ? appState.labReport!.biomarkers.map(mapBiomarkerToBarMarker)
    : DEMO_MARKERS;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [tickerMarkers, setTickerMarkers] = useState<BarMarker[]>([]);
  const [symptoms, setSymptoms] = useState<BarSymptom[]>([]);
  const [captainMsg, setCaptainMsg] = useState<string | null>(null);
  const [protocol, setProtocol] = useState<BarProtocol | null>(null);
  const [showCard, setShowCard] = useState(false);
  const [demoComplete, setDemoComplete] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showPregnancy, setShowPregnancy] = useState(false);
  const pregnancyResolveRef = useRef<((status: ModifierSet['reproductiveStatus']) => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasStarted = useRef(false);
  const signalsRef = useRef<CapturedSignal[]>([]);
  const turnRef = useRef(10); // demo uses IDs up to ~107, live turns start at 10
  const chatHistoryRef = useRef<GeminiMessage[]>([]);
  const modifiersRef = useRef<ModifierSet>({ ...EMPTY_MODIFIER_SET });
  const nextIdRef = useRef(200);

  // Build protocol data from dataSource — use real biomarkers when live (Fix 5)
  const proto = dataSource.getProtocol(activeBiomarkers, { ...EMPTY_MODIFIER_SET });
  const barProducts = proto.protocol.map(p => {
    const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
    const monthlyPrice = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
    return { name: p.productName, monthlyPrice };
  });
  const totalMonthly = barProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);
  const dailyPrice = (totalMonthly / 30).toFixed(2);

  useEffect(() => {
    if (scrollRef.current) setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 80);
  }, [messages, isTyping, showCard, showPregnancy, demoComplete]);

  const addMsg = useCallback((msg: ChatMessage) => setMessages(prev => [...prev, msg]), []);
  const wait = (ms: number) => new Promise(r => setTimeout(r, ms));
  const typing = useCallback(async (ms: number) => { setIsTyping(true); await wait(ms); setIsTyping(false); }, []);

  // -- Demo auto-play --
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    // Resume from saved session — skip demo auto-play
    if (resumedChatRef.current && resumedChatRef.current.length > 0) {
      const restored: ChatMessage[] = resumedChatRef.current.map((m, i) => ({
        id: i + 1,
        role: m.role as 'nurse' | 'user' | 'system',
        text: m.content,
      }));
      setMessages(restored);
      setTickerMarkers(activeMarkers);
      // Show protocol card if patient was far enough in the conversation
      if (restored.length >= 8) setShowCard(true);
      setDemoComplete(true);
      return;
    }

    // Live mode: skip scripted demo — show real data immediately (Fix 4)
    if (dataSource.mode === 'live') {
      // Show real biomarkers in ticker
      setTickerMarkers(activeMarkers);

      // Seed modifiers from orchestrator (real patient data from triage)
      if (orchestrator) {
        const orchModifiers = orchestrator.getModifiers();
        modifiersRef.current = { ...modifiersRef.current, ...orchModifiers };
      }

      // Build real protocol with real biomarkers + real modifiers
      const realProto = dataSource.getProtocol(activeBiomarkers, modifiersRef.current);
      const realBarProducts = realProto.protocol.map(p => {
        const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
        const mp = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
        return { name: p.productName, monthlyPrice: mp };
      });
      const realTotal = realBarProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);

      // Set protocol bar with real products and price
      setProtocol({
        gateLabel: `GATE ${realProto.gateLevel} · PROTOCOL`,
        products: realBarProducts,
        dailyPrice: realTotal / 30,
      });

      // Show intro message
      addMsg({ id: 1, role: 'nurse', text: "I've analyzed your lab results. Here's what I found." });
      setShowCard(true);
      setDemoComplete(true);

      if (onProtocolStart) {
        onProtocolStart(realProto);
      }
      return;
    }

    (async () => {
      await wait(600);

      let signals: CapturedSignal[] = [];

      // ---- PHASE 1: SEEN ----
      await typing(1000);
      addMsg({ id: 1, role: 'nurse', text: "Jimmy and Hank can communicate." });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: "Jimmy and Hank can communicate." }] });
      await wait(2200);

      const patientMsg1 = "I'm exhausted all the time. My doctor ran bloodwork and said everything is normal but I feel terrible.";
      addMsg({ id: 3, role: 'user', text: patientMsg1 });
      chatHistoryRef.current.push({ role: 'user', parts: [{ text: patientMsg1 }] });
      await wait(700);

      signals = await dataSource.extractSymptoms(patientMsg1, 1, signals);
      const allSymptoms = signalsToBarSymptoms(signals);

      if (allSymptoms.length > 0) {
        setSymptoms([allSymptoms[0]]);
        await wait(1000);
        if (allSymptoms.length > 1) {
          setSymptoms(allSymptoms.slice(0, 2));
        }
      }
      await wait(1800);

      const nurseMsg2 = "You're not imagining it. \"Normal\" ranges are built from a population that's 60% overweight. Let me show you what's actually going on \u2014 do you have your lab results handy?";
      await typing(1000);
      addMsg({ id: 4, role: 'nurse', text: nurseMsg2 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg2 }] });
      await wait(2200);

      const patientMsg2 = 'Yes, I have my LabCorp results from last month.';
      addMsg({ id: 6, role: 'user', text: patientMsg2 });
      chatHistoryRef.current.push({ role: 'user', parts: [{ text: patientMsg2 }] });
      await wait(800);
      addMsg({ id: 100, role: 'system', text: 'Lab results received \u2014 LabCorp \u00B7 Jan 14, 2026' });
      await wait(500);

      // ---- PHASE 2: RIGHT ----
      await typing(600);
      addMsg({ id: 101, role: 'nurse', text: 'Running your panel now.' });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: 'Running your panel now.' }] });
      await wait(800);

      setCaptainMsg('Analyzing 12 biomarkers against functional optimal ranges');
      await wait(3000);
      setCaptainMsg(null);
      await wait(300);

      for (let i = 0; i < 3; i++) {
        setTickerMarkers(prev => [...prev, { ...DEMO_MARKERS[i], flag: 'pending' as BarMarker['flag'] }]);
        await wait(200);
        setTickerMarkers(prev => prev.map((m, mi) => mi === i ? { ...DEMO_MARKERS[i] } : m));
        await wait(400);
      }
      await wait(600);

      for (let i = 3; i < DEMO_MARKERS.length; i++) {
        setTickerMarkers(prev => [...prev, { ...DEMO_MARKERS[i], flag: 'pending' as BarMarker['flag'] }]);
        await wait(120);
        setTickerMarkers(prev => prev.map((m, mi) => mi === i ? { ...DEMO_MARKERS[i] } : m));
        await wait(200);
      }
      await wait(400);

      setSymptoms(prev => {
        const hasBrainFog = prev.some(s => s.term === 'BRAIN FOG');
        if (hasBrainFog) return prev;
        return [...prev, { term: 'BRAIN FOG', linkedBiomarker: 'Ferritin' }];
      });
      await wait(1200);

      setCaptainMsg('3 markers flagged \u00B7 your doctor called them normal');
      await wait(3500);
      setCaptainMsg(null);
      await wait(600);

      const nurseMsg3 = "Found it. Three markers your doctor called normal are actually suboptimal: TSH, Vitamin D, and Ferritin. By functional medicine ranges \u2014 the ranges where you actually feel good \u2014 all three are significantly low.";
      await typing(1200);
      addMsg({ id: 104, role: 'nurse', text: nurseMsg3 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg3 }] });
      await wait(2000);

      const nurseMsg4 = "Your TSH alone at 3.8 \u2014 optimal is under 2.0 \u2014 explains the fatigue and the brain fog. You were right. Something is off.";
      await typing(1000);
      addMsg({ id: 105, role: 'nurse', text: nurseMsg4 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg4 }] });
      await wait(2500);

      // ---- PREGNANCY SCREENING (clinical safety gate) ----
      const pregnancyNurseMsg = "Before I put together your protocol, I need to check \u2014 are you currently pregnant, breastfeeding, or planning a pregnancy?";
      await typing(800);
      addMsg({ id: 108, role: 'nurse', text: pregnancyNurseMsg });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: pregnancyNurseMsg }] });
      await wait(400);

      setShowPregnancy(true);
      const reproStatus = await new Promise<ModifierSet['reproductiveStatus']>(resolve => {
        pregnancyResolveRef.current = resolve;
      });
      modifiersRef.current.reproductiveStatus = reproStatus;
      setShowPregnancy(false);

      // Record patient's answer in chat
      const pregnancyLabels: Record<ModifierSet['reproductiveStatus'], string> = {
        'pregnant': "Yes, I\u2019m pregnant",
        'trying_to_conceive': 'Trying to conceive',
        'breastfeeding': 'Breastfeeding',
        'postmenopausal': 'Postmenopausal',
        'not_applicable': 'None of these apply',
      };
      addMsg({ id: 109, role: 'user', text: pregnancyLabels[reproStatus] });
      chatHistoryRef.current.push({ role: 'user', parts: [{ text: pregnancyLabels[reproStatus] }] });
      await wait(300);

      if (reproStatus !== 'not_applicable') {
        await typing(600);
        addMsg({ id: 110, role: 'nurse', text: "Got it. I\u2019ll make sure your protocol only includes supplements that are safe for you." });
        chatHistoryRef.current.push({ role: 'model', parts: [{ text: "Got it. I\u2019ll make sure your protocol only includes supplements that are safe for you." }] });
        await wait(1000);
      } else {
        await typing(400);
        addMsg({ id: 110, role: 'nurse', text: "Thanks for confirming." });
        chatHistoryRef.current.push({ role: 'model', parts: [{ text: "Thanks for confirming." }] });
        await wait(600);
      }

      // ---- PHASE 3: SAFE ----
      // Rebuild protocol with pregnancy modifiers applied
      const safeProto = dataSource.getProtocol([], modifiersRef.current);
      const safeBarProducts = safeProto.protocol.map(p => {
        const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
        const mp = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
        return { name: p.productName, monthlyPrice: mp };
      });
      const safeTotalMonthly = safeBarProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);

      setCaptainMsg('Gate 1 \u00B7 supplement protocol matched');
      await wait(2500);
      setCaptainMsg(null);
      await wait(400);

      setProtocol({
        gateLabel: 'GATE 1 \u00B7 SUPPLEMENT PROTOCOL',
        products: safeBarProducts,
        dailyPrice: safeTotalMonthly / 30,
      });
      await wait(600);

      const nurseMsg5 = `Good news \u2014 all three can be addressed with targeted supplements. No prescriptions. This is ${dataSource.brandDisplay.medicalDirectorName}'s protocol, based on 40 years of functional medicine practice. We retest in ${dataSource.brandDisplay.retestWeeks} weeks to see exactly how your numbers respond.`;
      await typing(1200);
      addMsg({ id: 106, role: 'nurse', text: nurseMsg5 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg5 }] });
      await wait(2000);

      // ---- PHASE 4: ACT ----
      const safeDailyPrice = (safeTotalMonthly / 30).toFixed(2);
      const nurseMsg6 = `${safeBarProducts.length} supplement${safeBarProducts.length !== 1 ? 's' : ''}. $${safeDailyPrice} a day. Most women feel the difference in 3 to 4 weeks.`;
      await typing(800);
      addMsg({ id: 111, role: 'nurse', text: nurseMsg6 });
      chatHistoryRef.current.push({ role: 'model', parts: [{ text: nurseMsg6 }] });
      await wait(400);

      setShowCard(true);

      if (onProtocolStart) {
        onProtocolStart(safeProto);
      }

      // Store signals for interactive mode
      signalsRef.current = signals;

      // Demo complete — enable input
      await wait(1500);
      setDemoComplete(true);

    })();
  }, [addMsg, typing]);

  // -- Interactive message handler --
  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isSending) return;

    setInputText('');
    setIsSending(true);

    const msgId = nextIdRef.current++;
    const turn = ++turnRef.current;

    // Add user message
    addMsg({ id: msgId, role: 'user', text });
    chatHistoryRef.current.push({ role: 'user', parts: [{ text }] });

    // Extract symptoms from user message
    try {
      const newSignals = await dataSource.extractSymptoms(text, turn, signalsRef.current);
      if (newSignals.length > signalsRef.current.length) {
        signalsRef.current = newSignals;
        setSymptoms(signalsToBarSymptoms(newSignals));
      }
    } catch {
      // Extraction failure is non-blocking
    }

    // NRS.06: Scan for disclosures via orchestrator (when present)
    if (orchestrator) {
      orchestrator.scanForDisclosures(text, turn);
    }

    // Check for late reveals (medication, allergy, condition after protocol)
    const reveal = dataSource.detectLateReveal(text, turn);
    if (reveal && reveal.needsRerun) {
      // When orchestrator is present, merge its modifiers for complete picture
      const updatedMods = orchestrator
        ? { ...reveal.updatedModifiers, ...orchestrator.getModifiers() }
        : reveal.updatedModifiers;
      modifiersRef.current = updatedMods;

      // Re-run protocol with updated modifiers
      const newProto = dataSource.getProtocol(activeBiomarkers, updatedMods);
      const newBarProducts = newProto.protocol.map(p => {
        const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
        const mp = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
        return { name: p.productName, monthlyPrice: mp };
      });
      const newTotal = newBarProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);

      setCaptainMsg('Protocol updated \u00B7 safety modifier detected');
      setProtocol({
        gateLabel: `GATE ${newProto.gateLevel ?? 1} \u00B7 UPDATED PROTOCOL`,
        products: newBarProducts,
        dailyPrice: newTotal / 30,
      });

      // Show system message about what changed
      addMsg({
        id: nextIdRef.current++,
        role: 'system',
        text: `Safety update: ${reveal.safetyChanges.join(', ')} detected`,
      });

      setTimeout(() => setCaptainMsg(null), 3000);
    }

    // Build system prompt with full context + reinforcement
    const reinforcement = dataSource.getNurseReinforcement(signalsRef.current, turn);
    // Use real biomarker data and real protocol for system prompt when live
    const currentProto = dataSource.getProtocol(activeBiomarkers, modifiersRef.current);
    const systemPrompt = buildNurseSystemPrompt(
      activeBiomarkers,
      currentProto,
      signalsRef.current,
      'HemoLogix',
      dataSource.brandDisplay.medicalDirectorName,
      reinforcement,
    );

    // Call Gemini for nurse response
    setIsTyping(true);
    try {
      const response = await dataSource.nurseRespond(
        chatHistoryRef.current,
        systemPrompt,
        text,
      );

      setIsTyping(false);
      if (response) {
        // Strip modal markers from displayed text (orchestrator handles them)
        let displayText = response;
        if (orchestrator) {
          const markerPatterns = [
            '[SHOW_GOALS_MODAL]', '[SHOW_MEDS_MODAL]',
            '[SHOW_ALLERGIES_MODAL]', '[SHOW_PREGNANCY_MODAL]',
            '[SHOW_REPORT]', '[BUILD_PROTOCOL]', '[SHOW_EMAIL_MODAL]',
          ];
          for (const marker of markerPatterns) {
            if (displayText.includes(marker)) {
              displayText = displayText.replace(marker, '').trim();
              // Check if this marker should trigger a modal
              const modalCheck = orchestrator.shouldShowModal(marker);
              if (modalCheck.show && modalCheck.modalType === 'pregnancy') {
                setShowPregnancy(true);
              }
              if (marker === '[SHOW_EMAIL_MODAL]') {
                setShowEmailModal(true);
              }
              // [BUILD_PROTOCOL] triggers protocol with real modifiers
              if (marker === '[BUILD_PROTOCOL]') {
                const realModifiers = orchestrator.getModifiers();
                modifiersRef.current = { ...modifiersRef.current, ...realModifiers };
                const realProto = dataSource.getProtocol(activeBiomarkers, modifiersRef.current);
                const realBarProducts = realProto.protocol.map(p => {
                  const seed = ALL_PRODUCTS_FOR_PRICE.find(s => s.id === p.productId);
                  const mp = parseMonthlyPrice(seed?.pricing?.monthlyPrice);
                  return { name: p.productName, monthlyPrice: mp };
                });
                const realTotal = realBarProducts.reduce((sum, p) => sum + p.monthlyPrice, 0);
                setProtocol({
                  gateLabel: `GATE ${realProto.gateLevel ?? 2} \u00B7 PROTOCOL`,
                  products: realBarProducts,
                  dailyPrice: realTotal / 30,
                });
                setShowCard(true);
                if (onProtocolStart) onProtocolStart(realProto);
              }
            }
          }
        }
        addMsg({ id: nextIdRef.current++, role: 'nurse', text: displayText });
        chatHistoryRef.current.push({ role: 'model', parts: [{ text: displayText }] });
      }
    } catch (err) {
      setIsTyping(false);
      console.error('[PatientChatEngine] nurseRespond error:', err);
      addMsg({ id: nextIdRef.current++, role: 'system', text: 'Connection issue \u2014 try again' });
    }

    setIsSending(false);
    // Focus input after response
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [inputText, isSending, addMsg, dataSource, orchestrator]);

  // Focus input when demo completes
  useEffect(() => {
    if (demoComplete) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [demoComplete]);

  return (
    <div style={{ width: '100%', maxWidth: 480, height: '100vh', margin: '0 auto', display: 'flex', flexDirection: 'column', background: T.bg, fontFamily: F.body, position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0} body{margin:0;background:${T.bg}}
        ::-webkit-scrollbar{width:0;height:0}
        @keyframes td{0%,60%,100%{opacity:.2;transform:translateY(0)}30%{opacity:.8;transform:translateY(-3px)}}
        @keyframes msgIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: T.bgCard, borderBottom: `1px solid ${T.border}`, boxShadow: T.shadowSm, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><HeartPulseIcon /></div>
          <div>
            <div style={{ fontSize: T.fontSize.base, fontWeight: 700, color: T.text }}>Hemo<span style={{ color: T.accent }}>Logix</span></div>
            <div style={{ fontSize: T.fontSize.xs, color: T.textGhost, display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.success }} /> Health Navigator
            </div>
          </div>
        </div>
        <div style={{ fontSize: T.fontSize.xs, fontWeight: 600, color: T.textGhost, display: 'flex', alignItems: 'center', gap: 4 }}><ShieldIcon /> HIPAA</div>
      </div>

      <AnalystBar markers={tickerMarkers} symptoms={symptoms} captainMsg={captainMsg} protocol={protocol} journeyStage="first_visit" />

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ textAlign: 'center', padding: '4px 0 12px' }}>
          <div style={{ display: 'inline-block', fontSize: T.fontSize.xs, fontWeight: 600, color: T.textGhost, background: T.bgSoft, padding: '4px 12px', borderRadius: 20 }}>Free AI Lab Analysis</div>
        </div>

        {messages.map(m => {
          if (m.role === 'system') return (
            <div key={m.id} style={{ textAlign: 'center', padding: '8px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: T.fontSize.sm, color: T.success, fontWeight: 600, background: T.successSoft, padding: '6px 14px', borderRadius: 20 }}><FileCheckIcon /> {m.text}</div>
            </div>
          );
          if (m.role === 'user') return (
            <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-end', padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ maxWidth: '82%', padding: '10px 14px', background: T.accent, color: CONTRAST_WHITE, borderRadius: `${R_LG}px ${R_LG}px ${R}px ${R_LG}px`, fontSize: T.fontSize.sm, lineHeight: 1.55 }}>
                <HighlightedText text={m.text} activeSymptoms={symptoms} />
              </div>
            </div>
          );
          if (m.role === 'nurse') return (
            <div key={m.id} style={{ padding: '3px 0', animation: 'msgIn 0.3s ease' }}>
              <div style={{ maxWidth: '88%', padding: '10px 14px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: `${R_LG}px ${R_LG}px ${R_LG}px ${R}px`, fontSize: T.fontSize.sm, lineHeight: 1.6, color: T.text, boxShadow: T.shadowSm }}>{m.text}</div>
            </div>
          );
          return null;
        })}

        {showPregnancy && (
          <div style={{ padding: '3px 0' }}>
            <PregnancyModal onSubmit={(status) => {
              if (pregnancyResolveRef.current) {
                pregnancyResolveRef.current(status);
                pregnancyResolveRef.current = null;
              }
            }} />
          </div>
        )}

        {showCard && <ProtocolCard dataSource={dataSource} dailyPrice={dailyPrice} />}

        {demoComplete && <SaveProgressBanner messageCount={messages.length} />}

        {isTyping && (
          <div style={{ padding: '3px 0', animation: 'msgIn 0.2s ease' }}>
            <div style={{ display: 'inline-flex', padding: '10px 14px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: `${R_LG}px ${R_LG}px ${R_LG}px ${R}px`, boxShadow: T.shadowSm }}><TypingDots /></div>
          </div>
        )}
        <div style={{ height: 8 }} />
      </div>

      {/* Footer — input field after demo, disclaimer during */}
      {demoComplete ? (
        <div style={{
          padding: '10px 12px 12px', background: T.bgCard, borderTop: `1px solid ${T.border}`,
          animation: 'fadeIn 0.4s ease',
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about your results, protocol, side effects..."
              disabled={isSending}
              style={{
                flex: 1, padding: '10px 14px', fontSize: T.fontSize.sm,
                border: `1px solid ${T.border}`, borderRadius: R_LG,
                outline: 'none', fontFamily: F.body,
                color: T.text, background: T.bg,
              }}
            />
            <button
              onClick={handleSend}
              disabled={isSending || !inputText.trim()}
              style={{
                width: 40, height: 40, borderRadius: '50%',
                background: inputText.trim() ? T.accent : T.border,
                border: 'none', cursor: inputText.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.2s ease',
                flexShrink: 0,
              }}
            >
              <SendIcon />
            </button>
          </div>
          <div style={{ fontSize: T.fontSize.xs, color: T.textGhost, textAlign: 'center', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <ShieldIcon /> Not for diagnostic use &middot; Not a substitute for medical advice
          </div>
        </div>
      ) : (
        <div style={{ padding: '10px 12px 12px', background: T.bgCard, borderTop: `1px solid ${T.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: T.fontSize.xs, color: T.textGhost, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}><ShieldIcon /> Not for diagnostic use &middot; Not a substitute for medical advice</div>
        </div>
      )}
    </div>
  );
}
